import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { IChangePasswordModel, UserService } from '@libs/vc-core-lib';
import { translate, TranslocoPipe } from '@jsverse/transloco';
import { finalize, take } from 'rxjs';
import { DialogComponent, FormComponent, NotificationService, PasswordComponent } from 'vcp-common-ui-lib';

@Component({
    selector: 'vc-change-password-dialog',
    templateUrl: './change-password-dialog.component.html',
    styleUrls: ['./change-password-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslocoPipe, DialogComponent, FormComponent, PasswordComponent],
})
export class ChangePasswordDialogComponent {
    private readonly _userService: UserService = inject(UserService);
    private readonly _notificationService: NotificationService = inject(NotificationService);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    resetPasswordText = translate('ACCOUNT.RESET_PASSWORD');
    changePasswordText = translate('ACCOUNT.CHANGE_PASSWORD');
    changeText = translate('ACCOUNT.CHANGE');

    loading = false;

    currentPassword = '';
    newPassword = '';
    confirmPassword = '';

    @Input()
    visible = false;

    @Input()
    username!: string;

    /* Whether this dialog should be forcing user to set new password (true) or changing the password is optional (false by default) */
    @Input()
    forceReset = false;

    /* Emitted when after password API call.  True if success, false if error.  */
    @Output()
    passwordChange = new EventEmitter<boolean>();

    @Output()
    visibleChange = new EventEmitter<boolean>();

    @ViewChild('changePasswordForm')
    changePasswordForm!: FormComponent<never>;

    onChangePassword(valid: boolean) {
        if (!valid) return;

        this.loading = true;
        this._changeDetectorRef.markForCheck();

        const model: IChangePasswordModel = {
            oldPassword: this.currentPassword,
            newPassword: this.newPassword,
        };

        this._userService
            .changeMyPassword(model)
            .pipe(
                take(1),
                finalize(() => {
                    this.loading = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    this._notificationService.success(translate('ACCOUNT.SUCCESS_CHANGE_PASSWORD'));
                    this.passwordChange.emit(true);
                },
                error: () => {
                    this._notificationService.error(translate('ACCOUNT.DETAILS.ERROR_SAVING_PASSWORD'));
                    this.passwordChange.emit(false);
                },
            });
    }

    onPasswordInput() {
        this._changeDetectorRef.markForCheck();
    }
}
